import { useNavigate, useParams } from 'react-router-dom';
import { Pencil } from 'lucide-react';
import { Button, Card, CardHeader, CardTitle, CardBody, Chip } from '@vnetwork/ui/primitives';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import { useItemModel, type Specification } from '@/api/capacities';
import type { InfoItem } from '@/pages/account/components/InfoGrid';
import { MasterDetailScaffold } from '../shared/master-detail-scaffold';
import { formatWhDateTime } from '../shared/wh-format';
import { activeStatusCell, itemTypeCell, itemTypeLabel } from '../shared/masters-status';

const specColumns: ColumnDef<Specification, unknown>[] = [
  { id: 'name', header: 'Name', cell: ({ row }) => row.original.name || '—' },
  { id: 'value', header: 'Value', cell: ({ row }) => row.original.value || '—' },
  { id: 'unit', header: 'Unit', cell: ({ row }) => row.original.unit || '—' },
  {
    id: 'primary',
    header: 'Primary',
    cell: ({ row }) =>
      row.original.is_primary ? (
        <Chip tone="accent">Primary</Chip>
      ) : (
        <span style={{ color: 'var(--vn-fg-muted)' }}>—</span>
      ),
  },
];

export interface CapacityModelDetailPageProps {
  /** Model id. Falls back to the `:id` route param when omitted. */
  id?: string;
}

/**
 * Item Model detail — identity header (`#model` + name + type/active) over an
 * info card (model · name · type · category · capacity · description). The
 * Specifications card renders only when the detail payload carries a
 * `specifications` array (absent on list rows). Read-only.
 */
export default function CapacityModelDetailPage({ id: idProp }: CapacityModelDetailPageProps) {
  const params = useParams();
  const navigate = useNavigate();
  const id = idProp ?? params.id ?? '';

  const { data: model, isLoading, isError, refetch } = useItemModel(id);
  const brand = (model as typeof model & { brand?: string })?.brand;

  // Legacy: Model Name/Code/Item Type/Category always; Brand/Capacity/Description
  // only when present; then Status + audit dates (en-US).
  const items: InfoItem[] = [
    { label: 'Model Name', value: model?.name || '—' },
    { label: 'Model Code', value: model?.model || '—' },
    { label: 'Item Type', value: model ? itemTypeLabel(model.item_type) : undefined },
    { label: 'Category', value: model?.category || '—' },
    ...(brand ? [{ label: 'Brand', value: brand }] : []),
    ...(model?.capacity ? [{ label: 'Capacity', value: model.capacity }] : []),
    { label: 'Status', value: activeStatusCell(model?.is_active) },
    ...(model?.description ? [{ label: 'Description', value: model.description, full: true }] : []),
    { label: 'Created At', value: formatWhDateTime(model?.created_at) },
    { label: 'Updated At', value: formatWhDateTime(model?.updated_at) },
  ];

  const specs = model?.specifications ?? [];

  return (
    <MasterDetailScaffold
      isLoading={isLoading}
      isError={isError}
      onRetry={() => refetch()}
      loaded={!!model}
      backTo="/capacities/models"
      backLabel="Models"
      codePrefix={model?.model ? `#${model.model}` : undefined}
      title={model?.name ?? ''}
      status={
        <>
          {model ? itemTypeCell(model.item_type) : null}
          {activeStatusCell(model?.is_active)}
        </>
      }
      actions={
        <Button
          variant="secondary"
          startIcon={<Pencil size={15} />}
          onClick={() => navigate(`/capacities/models/${id}/edit`)}
        >
          Edit
        </Button>
      }
      cardTitle="Model Information"
      items={items}
    >
      <Card flat>
        <CardHeader>
          <CardTitle>Specifications ({specs.length})</CardTitle>
        </CardHeader>
        <CardBody>
          <DataTable
            columns={specColumns}
            data={specs}
            getRowId={(s, i) => s.id ?? String(i)}
            enableSorting={false}
            emptyState={
              <EmptyState size="sm" title="No specifications" description="No specifications defined for this model." />
            }
          />
        </CardBody>
      </Card>
    </MasterDetailScaffold>
  );
}
